// Path: ./src/app/pages/UploadFlyers/index.tsx
import { Box, Stack } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteIcon from "@mui/icons-material/Delete";
import { SearchInput } from "../../components/SearchInputField";
import { adminApiService } from "../../../utils/api";
import {
  GfFlyer,
  GfFlyerItemList,
  MuiFilterRequestDto,
  AiGenerationStatus,
} from "@swagger/typescript-fetch-goflyer";

import { GridFilterModel, GridRowSelectionModel } from "@mui/x-data-grid";
import { MuiConfirmationModal } from "../../components/MuiConfirmationModal";
import { useTranslation } from "react-i18next";

import { messages } from "../../../locales/messages";
import { useNavigate } from "react-router-dom";
import { toastError, toastSuccess } from "../../../utils/pageUtil/toast";
import { TableData } from "../../../utils/interface/tableData";
import { useFlyerTableColumns } from "./useFlyerTableColumns";
import { SetupPageTopAppBar } from "../../../utils/commonStyledComponents/commonStyled";
import { BlackFontTransparentButton } from "../../../utils/commonStyledComponents/commonMuiStyled";
import { LeftButtonSection, RightSearchSection, TableWrapper } from "./styled";
import { GoFlyerAdminContext } from "../../../contextApi/context";
import { SUPERADMIN } from "../../../utils/constant/stringConstant";
import { useAuthHeaders } from "../../../hooks/useAuthHeaders";
import { useQueryParams } from "../../../hooks/useQueryParams";
import { ListTableQueryPagination } from "../../components/ListTable/ListTableQueyPagination";
import { muiFilterModelHasActiveItems } from "../../../utils/muiGridFilterModel";
import { useLatestRequestGuard } from "../../../hooks/useLatestRequestGuard";
import { guardedListFetch } from "../../../utils/guardedListFetch";
import { AiErrorLogModal } from "../CreateFlyerItemListPage/AiErrorLogModal";
import { RetryCoordinatesConfirmDescription } from "../../components/RetryCoordinatesConfirmDescription";
import { makeStoreDisplayName } from "../../../utils/dtoUtil/makeStoreDisplayName";
import { useTableSettingsConfig } from "../../../hooks/useTableSettingsConfig";
import { SettingKey } from "@swagger/typescript-fetch-goflyer";

const UploadFlyer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state } = useContext(GoFlyerAdminContext);
  const authHeaders = useAuthHeaders();

  const { pageLimit } = useTableSettingsConfig(SettingKey.TableFlyers, 20);

  const {
    paginationModel,
    sortModel,
    searchText,
    setQueryParams,
    handleTableChange,
  } = useQueryParams(pageLimit);

  const [paginationLoading, setPaginationLoading] = useState(false);
  const listRequestGuard = useLatestRequestGuard();
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);
  const [flyerListData, setFlyerListData] = useState<TableData<GfFlyer>>();
  const [open, setOpen] = useState(false);
  const [openErrorLogModal, setOpenErrorLogModal] = useState<boolean>(false);
  const [selectedItemList, setSelectedItemList] =
    useState<GfFlyerItemList | null>(null);
  const [generatingIds, setGeneratingIds] = useState<string[]>([]);
  const [retryingIds, setRetryingIds] = useState<string[]>([]);
  const [retryingCoordinateIds, setRetryingCoordinateIds] = useState<string[]>(
    [],
  );
  const [showRetryCoordinatesConfirmDialog, setShowRetryCoordinatesConfirmDialog] =
    useState(false);
  const [selectedFlyerForRetryCoordinates, setSelectedFlyerForRetryCoordinates] =
    useState<GfFlyer | null>(null);
  const [showGenerateConfirmDialog, setShowGenerateConfirmDialog] =
    useState(false);
  const [showRetryTileJobConfirmDialog, setShowRetryTileJobConfirmDialog] =
    useState(false);
  const [selectedFlyerForGeneration, setSelectedFlyerForGeneration] =
    useState<GfFlyer | null>(null);
  const [selectedFlyerForRetryTileJob, setSelectedFlyerForRetryTileJob] =
    useState<GfFlyer | null>(null);

  /**
   * Opens the modal for viewing the AI Generation Error Log and run details.
   */
  const handleErrorLogClick = (itemList: GfFlyerItemList) => {
    setSelectedItemList(itemList);
    setOpenErrorLogModal(true);
  };

  const handleGenerateCoordinates = (flyer: GfFlyer) => {
    setSelectedFlyerForGeneration(flyer);
    setShowGenerateConfirmDialog(true);
  };

  const handleConfirmGenerateCoordinates = async () => {
    if (!selectedFlyerForGeneration?.id) return;
    if (generatingIds.includes(selectedFlyerForGeneration.id)) return;

    const flyerId = selectedFlyerForGeneration.id;
    setShowGenerateConfirmDialog(false);
    setGeneratingIds((prev) => [...prev, flyerId]);
    try {
      const result =
        await adminApiService.gfFlyerAdminControllerGenerateCoordinatesFromFlyer(
          flyerId,
          authHeaders,
        );
      if (!result?.status) return;

      const updatedFlyer =
        await adminApiService.getOneBaseGfFlyerAdminControllerGfFlyer(flyerId);
      setFlyerListData((prev) => ({
        ...prev,
        rows: prev?.rows?.map((row) =>
          row.id === flyerId ? updatedFlyer : row,
        ),
      }));

      toastSuccess(t(messages.Generate_Coordinates()));
    } catch (error) {
      console.log("error", error);
      toastError(t(messages.There_is_an_error_please_Contact_Administrator()));
    } finally {
      setGeneratingIds((prev) => prev.filter((id) => id !== flyerId));
      setSelectedFlyerForGeneration(null);
    }
  };

  const handleCancelGenerateCoordinates = () => {
    setShowGenerateConfirmDialog(false);
    setSelectedFlyerForGeneration(null);
  };

  const handleRetryTileJob = (flyer: GfFlyer) => {
    setSelectedFlyerForRetryTileJob(flyer);
    setShowRetryTileJobConfirmDialog(true);
  };

  const handleConfirmRetryTileJob = async () => {
    if (!selectedFlyerForRetryTileJob?.id) return;
    if (retryingIds.includes(selectedFlyerForRetryTileJob.id)) return;

    const flyerId = selectedFlyerForRetryTileJob.id;
    setShowRetryTileJobConfirmDialog(false);
    setRetryingIds((prev) => [...prev, flyerId]);

    try {
      await adminApiService.gfFlyerCroppedImageAdminControllerRegenerate(
        flyerId,
        authHeaders,
      );

      const updatedFlyer =
        await adminApiService.getOneBaseGfFlyerAdminControllerGfFlyer(flyerId);

      setFlyerListData((prev) => ({
        ...prev,
        rows: prev?.rows?.map((row) =>
          row.id === flyerId ? updatedFlyer : row,
        ),
      }));

      toastSuccess(t(messages.Retry_Tile_Job()));
    } catch (error) {
      console.log("error", error);
      toastError(t(messages.There_is_an_error_please_Contact_Administrator()));
    } finally {
      setRetryingIds((prev) => prev.filter((id) => id !== flyerId));
      setSelectedFlyerForRetryTileJob(null);
    }
  };

  const handleCancelRetryTileJob = () => {
    setShowRetryTileJobConfirmDialog(false);
    setSelectedFlyerForRetryTileJob(null);
  };

  const updateFlyerRow = (flyerId: string, updater: (row: GfFlyer) => GfFlyer) => {
    setFlyerListData((prev) => ({
      ...prev,
      rows: prev?.rows?.map((row) =>
        row.id === flyerId ? updater(row) : row,
      ),
    }));
  };

  const handleRetryCoordinates = (flyer: GfFlyer) => {
    if (!flyer.id) return;
    if (retryingCoordinateIds.includes(flyer.id)) return;

    setSelectedFlyerForRetryCoordinates(flyer);
    setShowRetryCoordinatesConfirmDialog(true);
  };

  const handleCancelRetryCoordinates = () => {
    setShowRetryCoordinatesConfirmDialog(false);
    setSelectedFlyerForRetryCoordinates(null);
  };

  const handleConfirmRetryCoordinates = async () => {
    const flyerId = selectedFlyerForRetryCoordinates?.id;
    if (!flyerId) return;
    if (retryingCoordinateIds.includes(flyerId)) return;

    setShowRetryCoordinatesConfirmDialog(false);
    setRetryingCoordinateIds((prev) => [...prev, flyerId]);
    updateFlyerRow(flyerId, (currentRow) => ({
      ...currentRow,
      flyerItemList: currentRow.flyerItemList
        ? {
            ...currentRow.flyerItemList,
            aiGenerationStatus: AiGenerationStatus.WAITING,
            aiGenerationErrorLog: undefined,
          }
        : currentRow.flyerItemList,
    }));

    try {
      await adminApiService.gfFlyerAdminControllerRetryFlyerCoordinates(
        flyerId,
        authHeaders,
      );

      const updatedFlyer =
        await adminApiService.getOneBaseGfFlyerAdminControllerGfFlyer(flyerId);
      updateFlyerRow(flyerId, () => updatedFlyer);
      toastSuccess(t(messages.Retry_Coordinates()));
    } catch (error) {
      console.log("error", error);
      try {
        const updatedFlyer =
          await adminApiService.getOneBaseGfFlyerAdminControllerGfFlyer(
            flyerId,
          );
        updateFlyerRow(flyerId, () => updatedFlyer);
      } catch (refetchError) {
        console.log("refetch error", refetchError);
      }
      toastError(t(messages.There_is_an_error_please_Contact_Administrator()));
    } finally {
      setRetryingCoordinateIds((prev) => prev.filter((id) => id !== flyerId));
      setSelectedFlyerForRetryCoordinates(null);
    }
  };

  const flyerTableColumns = useFlyerTableColumns(
    handleErrorLogClick,
    handleGenerateCoordinates,
    generatingIds,
    handleRetryTileJob,
    retryingIds,
    handleRetryCoordinates,
    retryingCoordinateIds,
  );

  const handleOpenDelete = () => {
    setOpen(true);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const fetchFlyerList = async () => {
    await guardedListFetch(
      listRequestGuard,
      setPaginationLoading,
      async () => {
        const conditions = [];
        const storesList =
          state.user?.parsedRoles?.name === SUPERADMIN
            ? []
            : state.user?.stores?.map((value) => {
                return `${value.id}`;
              }) || [];

        if (storesList && storesList.length) {
          conditions.push(`stores.id||$in||${storesList?.join(",")}`);
        }

        return adminApiService.getManyBaseGfFlyerAdminControllerGfFlyer(
          undefined,
          undefined,
          conditions?.length ? conditions : undefined,
          undefined,
          sortModel?.length
            ? [`${sortModel[0].field},${sortModel[0]?.sort?.toUpperCase()}`]
            : [`updatedAt,DESC`],
          undefined,
          paginationModel?.pageSize,
          0,
          paginationModel?.page,
          undefined,
        );
      },
      (FlyerList) => {
        setFlyerListData((prev) => ({
          ...prev,
          rows: FlyerList.data,
          total: FlyerList?.total,
        }));
      },
    );
  };

  const handleDeleteFlyer = async () => {
    if (rowSelectionModel?.length) {
      try {
        setPaginationLoading(true);
        const result = rowSelectionModel?.map(async (value) => {
          return await adminApiService.deleteOneBaseGfFlyerAdminControllerGfFlyer(
            value as string,
            authHeaders,
          );
        });
        await Promise.all(result);

        if (searchText?.trim()?.length) {
          searchFlyerFn(searchText);
        } else {
          fetchFlyerList();
        }

        setPaginationLoading(false);
        handleCancel();
        toastSuccess(t(messages.Successfully_Deleted_Flyers()));
      } catch (error) {
        console.log("error", error);
        setPaginationLoading(false);
        toastError(
          t(messages.There_is_an_error_please_Contact_Administrator()),
        );
      }
    }
  };

  const handleOnSearch = (value: string) => {
    setQueryParams({
      searchText: value,
      page: 1,
      sortField: null,
      sortOrder: null,
    });
  };

  const fetchFlyerListWithFilter = async (
    activeFilterModel: GridFilterModel,
  ) => {
    await guardedListFetch(
      listRequestGuard,
      setPaginationLoading,
      async () => {
        const body: MuiFilterRequestDto = {
          filterModel: {
            items: activeFilterModel.items.map((item) => ({
              field: item.field,
              operator: item.operator,
              value: item.value,
              id: item.id,
            })),
            logicOperator: activeFilterModel.logicOperator as any,
            quickFilterValues: activeFilterModel.quickFilterValues,
            quickFilterLogicOperator:
              activeFilterModel.quickFilterLogicOperator as any,
          },
          page: paginationModel.page,
          pageSize: paginationModel.pageSize,
          sortModel: sortModel?.map((s) => ({
            field: s.field,
            sort: s.sort as any,
          })),
        };
        return adminApiService.gfFlyerAdminControllerFilterWithMuiGrid(
          body,
          authHeaders,
        );
      },
      (response) => {
        if (!response) return;
        setFlyerListData((prev) => ({
          ...prev,
          rows: response.rows,
          total: response.total,
        }));
      },
    );
  };

  const handleFilterModelChange = (newFilterModel: GridFilterModel) => {
    setFilterModel(newFilterModel);
  };

  const searchFlyerFn = async (searchValue: string) => {
    if (!searchValue?.length) return;

    await guardedListFetch(
      listRequestGuard,
      setPaginationLoading,
      async () =>
        adminApiService.gfFlyerAdminControllerSearch(
          searchValue,
          paginationModel.page,
          paginationModel.pageSize,
        ),
      (response) => {
        setFlyerListData((prev) => ({
          ...prev,
          rows: response?.data,
          total: response?.total,
        }));
      },
    );
  };

  useEffect(() => {
    setFlyerListData({
      columns: flyerTableColumns,
    });
  }, []);

  // The search endpoint can't sort, so search results are sorted in the grid.
  const isShowingSearchResults =
    !muiFilterModelHasActiveItems(filterModel) && Boolean(searchText?.length);

  useEffect(() => {
    const hasActiveFilters = muiFilterModelHasActiveItems(filterModel);
    if (hasActiveFilters) {
      fetchFlyerListWithFilter(filterModel);
    } else if (searchText?.length) {
      searchFlyerFn(searchText);
    } else {
      fetchFlyerList();
    }
  }, [searchText, paginationModel, sortModel, filterModel]);

  return (
    <Box data-testid="upload-flyer-view-page">
      <SetupPageTopAppBar>
        <LeftButtonSection>
          <Stack direction="row" spacing={2}>
            <BlackFontTransparentButton
              variant="text"
              startIcon={
                <UploadFileIcon style={{ color: "#ff8038", fontSize: 25 }} />
              }
              onClick={() => {
                navigate("/uploadflyer/create", {
                  state: {
                    uploadflyer: true,
                  },
                });
              }}
              data-testid="create-button"
            >
              {t(messages.Create())}
            </BlackFontTransparentButton>
            <BlackFontTransparentButton
              variant="text"
              startIcon={
                <DeleteIcon
                  style={{
                    color: "#ff8038",
                    fontSize: 25,
                    opacity: !rowSelectionModel?.length ? 0.5 : 1,
                  }}
                />
              }
              disabled={!rowSelectionModel?.length}
              onClick={handleOpenDelete}
              data-testid="delete-button"
            >
              {t(messages.Delete())}
            </BlackFontTransparentButton>
          </Stack>
        </LeftButtonSection>
        {(state.user?.parsedRoles?.name === SUPERADMIN ||
          !state.user?.stores?.length) && (
          <RightSearchSection>
            <SearchInput
              onSearch={handleOnSearch}
              initialValue={searchText}
            ></SearchInput>
          </RightSearchSection>
        )}
      </SetupPageTopAppBar>
      <TableWrapper>
        <ListTableQueryPagination
          loadFailed={listRequestGuard.loadFailed}
          sortingMode={isShowingSearchResults ? "client" : "server"}
          columns={flyerTableColumns}
          rows={flyerListData?.rows}
          total={paginationLoading ? 0 : flyerListData?.total}
          paginationModel={paginationModel}
          sortModel={sortModel}
          isLoading={paginationLoading}
          onTableChange={handleTableChange}
          dynamicHeightAdjuster={210}
          filterModel={filterModel}
          onFilterModelChange={handleFilterModelChange}
          handleRowSelection={(value) => {
            setRowSelectionModel(value);
          }}
        />
      </TableWrapper>

      <AiErrorLogModal
        open={openErrorLogModal}
        onClose={() => {
          setOpenErrorLogModal(false);
          setSelectedItemList(null);
        }}
        errorLog={selectedItemList?.aiGenerationErrorLog ?? null}
        runDetails={selectedItemList}
      />
      <MuiConfirmationModal
        open={showGenerateConfirmDialog}
        handleCancel={handleCancelGenerateCoordinates}
        handleConfirm={handleConfirmGenerateCoordinates}
        title={`${t(messages.Generate_Coordinates())}`}
        description={t(messages.Generate_Coordinates_Description())}
      />
      <MuiConfirmationModal
        open={showRetryTileJobConfirmDialog}
        handleCancel={handleCancelRetryTileJob}
        handleConfirm={handleConfirmRetryTileJob}
        title={`${t(messages.Retry_Tile_Job_Title())}`}
        description={t(messages.Retry_Tile_Job_Description())}
      />
      <MuiConfirmationModal
        open={showRetryCoordinatesConfirmDialog}
        handleCancel={handleCancelRetryCoordinates}
        handleConfirm={handleConfirmRetryCoordinates}
        title={`${t(messages.Retry_Coordinates_Title())}`}
        description={
          selectedFlyerForRetryCoordinates ? (
            <RetryCoordinatesConfirmDescription
              storeName={
                selectedFlyerForRetryCoordinates.stores?.[0]
                  ? makeStoreDisplayName(
                      selectedFlyerForRetryCoordinates.stores[0],
                    )
                  : undefined
              }
              validStartDate={
                selectedFlyerForRetryCoordinates.flyerItemList?.validStartDate ??
                selectedFlyerForRetryCoordinates.validStartDate
              }
              validEndDate={
                selectedFlyerForRetryCoordinates.flyerItemList?.validEndDate ??
                selectedFlyerForRetryCoordinates.validEndDate
              }
              imageCount={
                selectedFlyerForRetryCoordinates.sourceImageCount ??
                selectedFlyerForRetryCoordinates.flyerItemList
                  ?.aiGenerationErrorLog?.totalImages
              }
            />
          ) : (
            t(messages.Retry_Coordinates_Description())
          )
        }
      />
      <MuiConfirmationModal
        open={open}
        handleCancel={handleCancel}
        handleConfirm={handleDeleteFlyer}
        title={`${t(messages.Delete_Confirmation())}`}
        description={`
        ${t(messages.Are_you_sure_you_want_to_delete_These())} ${t(
          messages.Selected_Flyers(),
          { count: rowSelectionModel.length },
        )}?  ${t(messages.This_action_cannot_be_undone())}.
      `}
      />
    </Box>
  );
};

export default UploadFlyer;
